/**
 * <sunday-abuy>: the accessory buy box (blocks/sunday-abuy.liquid).
 *
 * Sized accessory (protector, topper): width pills (name + "[w]″ wide"), length pills for that width.
 * Choices (an accessory whose record has a unit label, the pillow): its variants, then How many 1 or 2.
 * The price, the line beside it, the EMI line (from the threshold set in the block), the Add button and
 * the dock follow the choice. Start: the mattress in the cart, else ?variant=, else the size the site
 * remembers (@sunday/state "bed"; a custom size takes the standard size it is cut from), else the default.
 * Add goes through @sunday/cart: the line joins the bed of the same size (or, for a pillow, the first bed
 * without one), then the added sheet opens. Bubbles (EMI, B2B) open on tap.
 * Emits sunday:buy (dock: price, label) and sunday:abuy ({ w, l, productId, price } for the tiles).
 */
import { parseAll, uniq, label, cutFrom, tileName } from '@sunday/sizes';
import * as state from '@sunday/state';
import * as cart from '@sunday/cart';
import { paint as paintIn } from '@sunday/incart';
import { esc, money, emi, fill } from '@sunday/util';

const $ = (s, r) => r.querySelector(s);
const $$ = (s, r) => [...r.querySelectorAll(s)];

class SundayABuy extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    this.cfg = JSON.parse($('script[data-cfg]', this).textContent);
    const c = this.cfg;
    this.t = c.text;
    this.qty = 1;
    this.$price = $('[data-price]', this);
    this.$sub = $('[data-sub]', this);
    this.$add = $('[data-add]', this);
    this.$err = $('[data-err]', this);
    this.$emi = $('[data-emi]', this);

    if (c.options) {
      this.vid = c.selectedId;
      this.addEventListener('click', (e) => {
        const v = e.target.closest('[data-v]');
        if (v) {
          this.vid = Number(v.dataset.v);
          this.#press(v);
          this.render();
          this.#url();
          return;
        }
        const q = e.target.closest('[data-q]');
        if (q) {
          this.qty = Number(q.dataset.q);
          this.#press(q);
          this.render();
        }
      });
    } else {
      this.sizes = parseAll(c.variants);
      this.pick = this.#start();
      this.$w = $('[data-widths]', this);
      this.$l = $('[data-lengths]', this);
      this.addEventListener('click', (e) => {
        const w = e.target.closest('[data-w]');
        if (w) {
          const nw = Number(w.dataset.w);
          const ls = this.#lengths(nw);
          this.pick = { w: nw, l: ls.includes(this.pick.l) ? this.pick.l : ls.includes(78) ? 78 : ls[0] };
          this.#remember();
          this.render();
          return;
        }
        const l = e.target.closest('[data-l]');
        if (l) {
          this.pick = { w: this.pick.w, l: Number(l.dataset.l) };
          this.#remember();
          this.render();
        }
      });
    }
    this.$add.addEventListener('click', () => this.#addToCart());
    // The phone dock's Add (sunday-sheets.js clicks only <sunday-buy>): it adds from here.
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-dock-add]') && !document.querySelector('sunday-buy')) this.#addToCart();
    });
    this.#bubbles();
    this.render();
  }

  /* ---------- size ---------- */
  #start() {
    const c = this.cfg, S = this.sizes;
    const find = (w, l) => S.find((s) => s.w === w && s.l === l);
    if (c.cartSize) {
      const s = find(c.cartSize.w, c.cartSize.l) || cutFrom(S, c.cartSize.w, c.cartSize.l);
      if (s) return { w: s.w, l: s.l };
    }
    if (c.urlHasVariant) {
      const s = S.find((x) => x.id === c.selectedId);
      if (s) return { w: s.w, l: s.l };
    }
    const bed = state.get('bed');
    if (bed && bed.w && bed.l) {
      const s = find(bed.w, bed.l) || cutFrom(S, bed.w, bed.l);
      if (s) return { w: s.w, l: s.l };
    }
    const dm = /^(\d+)\s*x\s*(\d+)$/i.exec(c.defaultSize || '');
    const d = dm && find(+dm[1], +dm[2]);
    const s = d || S.find((x) => x.id === c.selectedId) || S[0];
    return { w: s.w, l: s.l };
  }

  #lengths(w) {
    return uniq(this.sizes.filter((s) => s.w === w).map((s) => s.l)).sort((a, b) => (a === 84) - (b === 84) || a - b);
  }

  #remember() {
    state.set('bed', { w: this.pick.w, l: this.pick.l, from: this.cfg.name });
    this.#url();
  }

  #url() {
    const v = this.chosen;
    if (!v) return;
    const url = new URL(location.href);
    url.searchParams.set('variant', v.id);
    history.replaceState(history.state, '', url);
  }

  get chosen() {
    if (this.cfg.options) return this.cfg.variants.find((v) => v.id === this.vid) || this.cfg.variants[0];
    return this.sizes.find((s) => s.w === this.pick.w && s.l === this.pick.l) || null;
  }

  #press(btn) {
    $$('button', btn.parentElement).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
  }

  /* ---------- paint ---------- */
  render() {
    const c = this.cfg, v = this.chosen;
    if (!c.options) {
      const ws = uniq(this.sizes.map((s) => s.w));
      this.$w.classList.toggle('sd-five', ws.length === 5);
      this.$w.innerHTML = ws
        .map((w) => {
          const s = this.sizes.find((x) => x.w === w);
          return `<button type="button" data-w="${w}" aria-pressed="${w === this.pick.w}"><b>${esc(tileName(s))}</b><small>${esc(fill(this.t.wide, { w }))}</small></button>`;
        })
        .join('');
      const ls = this.#lengths(this.pick.w);
      this.$l.style.setProperty('--n', ls.length);
      this.$l.innerHTML = ls.map((l) => `<button type="button" data-l="${l}" aria-pressed="${l === this.pick.l}">${l}″</button>`).join('');
    }
    if (!v) return;
    const total = v.price * this.qty;
    this.$price.textContent = money(total);
    const sub = c.options ? (this.qty > 1 ? fill(this.t.two, { price: money(v.price) }) : this.t.unit) : label(v);
    this.$sub.textContent = sub;
    this.$add.textContent = `${this.t.add} · ${money(total)}`;
    this.$add.disabled = v.available === false;
    // "− n in cart +" once this size is in the cart (@sunday/incart).
    this.$add.dataset.sdKind = 'a';
    this.$add.dataset.sdV = v.id;
    paintIn(this.$add);
    if (this.$emi) {
      const on = c.emiMonths > 0 && total >= c.emiFrom;
      this.$emi.hidden = !on;
      if (on) $('[data-emi-t]', this.$emi).textContent = fill(this.t.emi, { amount: money(emi(total, c.emiMonths)), months: c.emiMonths });
    }
    const detail = { ready: !!v, price: money(total), label: sub, variant: v.id, kind: 'a' };
    window.sundayBuyLast = detail;
    this.dispatchEvent(new CustomEvent('sunday:buy', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('sunday:abuy', { detail: { productId: c.productId, price: v.price, w: c.options ? null : v.w, l: c.options ? null : v.l } }));
  }

  /* ---------- add ---------- */
  async #addToCart() {
    const v = this.chosen, c = this.cfg;
    if (!v || this.busy) return;
    this.busy = true;
    this.$err.hidden = true;
    // The tap answers at once (sunday-sheets.js): the button says "Adding…"; then "− n in cart +" (@sunday/incart).
    const item = { btn: this.$add, qty: c.options ? this.qty : 1, name: c.name, what: this.$sub.textContent, price: money(v.price * this.qty), image: c.image, tint: c.tint || 'var(--sd-ground)' };
    document.dispatchEvent(new CustomEvent('sunday:adding', { detail: item }));
    try {
      let res;
      if (c.options) {
        // A pillow: the first bed with a mattress and no pillow yet, the quantity in one line.
        const now = await cart.get();
        const bed = cart
          .beds(now)
          .find((b) => b.key !== 'extras' && b.lines.some(cart.isMattress) && !b.lines.some((l) => Number(l.product_id) === Number(c.productId)));
        const properties = bed ? { _bed: bed.key } : {};
        const after = await cart.addItems([{ id: v.id, quantity: this.qty, properties }]);
        res = { cart: after, bed: properties._bed || null };
      } else {
        res = await cart.addFromProductPage({ variantId: v.id, productId: c.productId, size: { w: v.w, l: v.l } });
      }
      const line = res.cart.items.find((x) => x.variant_id === v.id && ((x.properties || {})._bed || null) === res.bed) || res.cart.items.find((x) => x.variant_id === v.id);
      document.dispatchEvent(
        new CustomEvent('sunday:added', {
          detail: { ...item, cart: res.cart, line, stay: true },
        })
      );
    } catch (err) {
      this.$err.textContent = err.message || '';
      this.$err.hidden = false;
      document.dispatchEvent(new CustomEvent('sunday:add-failed', { detail: { btn: this.$add, message: err.message || '' } }));
    } finally {
      this.busy = false;
    }
  }

  /* ---------- bubbles ---------- */
  #bubbles() {
    let pop = null;
    const close = () => {
      if (!pop) return;
      pop.remove();
      pop = null;
      $$('[data-tip][aria-expanded="true"]', this).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    };
    const open = (b) => {
      close();
      b.setAttribute('aria-expanded', 'true');
      pop = document.createElement('div');
      pop.className = 'sd-apop';
      pop.setAttribute('role', 'tooltip');
      pop.textContent = b.dataset.tip;
      document.body.appendChild(pop);
      const r = b.getBoundingClientRect();
      const w = Math.min(260, document.documentElement.clientWidth - 32);
      pop.style.width = w + 'px';
      const left = Math.max(16, Math.min(r.left + r.width / 2 - w / 2, document.documentElement.clientWidth - w - 16));
      pop.style.left = left + 'px';
      pop.style.top = r.bottom + 6 + 'px';
      pop.style.setProperty('--ax', r.left + r.width / 2 - left + 'px');
    };
    document.addEventListener(
      'click',
      (e) => {
        const b = e.target.closest && e.target.closest('[data-tip]');
        if (b && this.contains(b)) {
          e.preventDefault();
          if (b.getAttribute('aria-expanded') === 'true') close();
          else open(b);
          return;
        }
        if (!(e.target.closest && e.target.closest('.sd-apop'))) close();
      },
      true
    );
    document.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    document.addEventListener('scroll', close, true);
  }
}

if (!customElements.get('sunday-abuy')) customElements.define('sunday-abuy', SundayABuy);
